import { MovableDialog } from '@/components/shared/MovableDialog';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { msalInstance } from '@/lib/auth/msalConfig';
import { isAuthEnabled, env } from '@/env';

// Phase security-housekeeping (TODO-089) — 21 CFR Part 11 / SOC 2 idle
// timeout. Tracks user input across mousemove / mousedown / keydown /
// scroll / touchstart events. After `timeoutMinutes - warningMinutes` of
// inactivity a modal asks "Your session expires in N minutes — stay
// logged in?". A click on "Stay logged in" resets the timer. If the
// publisher does nothing for the remaining `warningMinutes`, MSAL is
// logged out, ending the session.
//
// When auth is disabled (dev mode) the component is inert — no timers
// run, no modal renders.

export interface SessionTimeoutWarningProps {
  /** Total idle minutes before logout. Default 60 (also overridable via VITE_SESSION_TIMEOUT_MINUTES). */
  timeoutMinutes?: number;
  /** Minutes-before-timeout that the warning modal appears. Default 5. */
  warningMinutes?: number;
  /**
   * Override for the logout function. Tests inject a spy; production
   * falls back to `msalInstance.logoutRedirect()`.
   */
  onLogout?: () => void;
  /**
   * Force enable / disable the timer regardless of `isAuthEnabled()`.
   * Used by tests to exercise the timer logic without mocking the env
   * module. Production callers should leave this unset.
   */
  enabled?: boolean;
}

const IDLE_EVENTS: ReadonlyArray<keyof globalThis.DocumentEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

function resolveTimeoutMinutes(prop: number | undefined): number {
  if (typeof prop === 'number' && Number.isFinite(prop) && prop > 0) return prop;
  const envVal = Number(env.VITE_SESSION_TIMEOUT_MINUTES);
  if (Number.isFinite(envVal) && envVal > 0) return envVal;
  return 60;
}

export function SessionTimeoutWarning({
  timeoutMinutes,
  warningMinutes = 5,
  onLogout,
  enabled,
}: SessionTimeoutWarningProps): ReactElement | null {
  const isEnabled = enabled ?? isAuthEnabled();
  const totalMs = resolveTimeoutMinutes(timeoutMinutes) * 60 * 1000;
  const warnMs = Math.max(0, warningMinutes) * 60 * 1000;
  const idleUntilWarning = Math.max(0, totalMs - warnMs);

  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(warningMinutes * 60);

  // Refs hold the timers so handlers can clear/reschedule without
  // re-running the effect on every state change.
  const warnTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const tickIntervalRef = useRef<ReturnType<typeof globalThis.setInterval> | null>(null);
  // Mirror of warningOpen for the activity handler. Reading state via
  // closure inside addEventListener would either bind to a stale value
  // or force an effect re-run on every modal open/close (which kills
  // the timers themselves). The ref pattern is the canonical fix.
  const warningOpenRef = useRef(false);
  warningOpenRef.current = warningOpen;

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) globalThis.clearTimeout(warnTimerRef.current);
    if (logoutTimerRef.current) globalThis.clearTimeout(logoutTimerRef.current);
    if (tickIntervalRef.current) globalThis.clearInterval(tickIntervalRef.current);
    warnTimerRef.current = null;
    logoutTimerRef.current = null;
    tickIntervalRef.current = null;
  }, []);

  const doLogout = useCallback(() => {
    clearTimers();
    setWarningOpen(false);
    if (onLogout) {
      onLogout();
      return;
    }
    // Production logout path. logoutRedirect is fire-and-forget — it
    // navigates the window away, ending any pending work.
    void msalInstance.logoutRedirect().catch(() => {
      // Swallow — the redirect itself replaces the page; an error here
      // means MSAL couldn't redirect (e.g. popup mode); we can't do much.
    });
  }, [clearTimers, onLogout]);

  const scheduleAll = useCallback(() => {
    clearTimers();
    setWarningOpen(false);
    setSecondsLeft(warningMinutes * 60);

    warnTimerRef.current = globalThis.setTimeout(() => {
      setWarningOpen(true);
      setSecondsLeft(Math.ceil(warnMs / 1000));
      tickIntervalRef.current = globalThis.setInterval(() => {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
      logoutTimerRef.current = globalThis.setTimeout(doLogout, warnMs);
    }, idleUntilWarning);
  }, [clearTimers, doLogout, idleUntilWarning, warnMs, warningMinutes]);

  const handleStayLoggedIn = useCallback(() => {
    scheduleAll();
  }, [scheduleAll]);

  // Mount-time wiring. Disabled entirely when auth is off (dev mode).
  useEffect(() => {
    if (!isEnabled) return undefined;

    scheduleAll();

    // Only RESET the timers on activity when the warning is NOT yet
    // showing. Once the modal is open, activity does NOT auto-extend
    // the session — the user must click "Stay logged in" explicitly.
    // This matches V1's behavior and the standard Part 11 pattern:
    // once warned, the choice is conscious.
    const handler = (): void => {
      if (warningOpenRef.current) return;
      scheduleAll();
    };
    for (const evt of IDLE_EVENTS) {
      document.addEventListener(evt, handler, { passive: true });
    }
    return (): void => {
      clearTimers();
      for (const evt of IDLE_EVENTS) {
        document.removeEventListener(evt, handler);
      }
    };
  }, [scheduleAll, isEnabled, clearTimers]);

  if (!warningOpen) return null;

  const minutesPart = Math.floor(secondsLeft / 60);
  const secondsPart = secondsLeft % 60;
  const display =
    minutesPart > 0
      ? `${minutesPart} minute${minutesPart === 1 ? '' : 's'}${
          secondsPart > 0 ? ` ${secondsPart}s` : ''
        }`
      : `${secondsPart} second${secondsPart === 1 ? '' : 's'}`;

  // phase-movable-dialogs — shared movable shell. No onClose: the timeout
  // warning must be answered, not dismissed with Escape.
  return (
    <MovableDialog
      persistKey="session.timeoutWarning"
      title={<span id="session-timeout-title">Session about to expire</span>}
      ariaLabelledBy="session-timeout-title"
      testId="session-timeout-modal"
      width="min(420px, 92vw)"
      zIndex={400}
    >
      <>

        <p style={{ margin: '0 0 16px 0', fontSize: 14, color: 'var(--text-muted, #555)' }}>
          Your session will end in{' '}
          <strong data-testid="session-timeout-remaining">{display}</strong>{' '}
          due to inactivity. Stay logged in?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="session-timeout-logout"
            onClick={doLogout}
            style={{
              background: 'none',
              color: 'var(--text-muted, #555)',
              border: '1px solid var(--border, #d4d4d8)',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Log out now
          </button>
          <button
            type="button"
            data-testid="session-timeout-stay"
            onClick={handleStayLoggedIn}
            style={{
              background: 'var(--accent, #1d4ed8)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stay logged in
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
